import { lazy, Suspense } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Zap, Shield, Clock, FileText, Box, FileSignature,
  CheckCircle2, Award, MapPin, Phone, Mail, Sparkles
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { motion } from 'framer-motion';
import heroImage from '@/assets/hero-walkin-sm.jpg';
import walkinRender from '@/assets/walkin-hero-sm.png';
import logo from '@/assets/logo.svg';
import { COMPANY_TRUST } from '@/lib/trustContent';
import Seo from "@/components/Seo";

const HeroProductViewer3D = lazy(() => import('@/components/viewer/HeroProductViewer3D'));

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] as const }
  })
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } }
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();





  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // Differentiating features first, consolidated to 6 strongest claims.
  const features = [
    { icon: Box, title: 'Interactive 3D Viewer', description: 'See your unit as you build it. Real-time 3D preview with door, equipment, and dimensions.' },
    { icon: FileSignature, title: 'Sign & Convert in Portal', description: 'Customers sign quotes digitally. Convert to a Purchase Order without ever leaving the app.' },
    { icon: Zap, title: 'Real-Time Dealer Pricing', description: 'Instant net pricing — base, refrigeration, options and freight, transparently itemized.' },
    { icon: FileText, title: 'Branded PDF Quotes', description: 'Auto-generated, brand-ready PDFs with your customer markup — download or email to your customer.' },
    { icon: Clock, title: 'Quick Ship Catalog', description: 'Pre-engineered units with accurate lead times. No back-and-forth, no email chains.' },
    { icon: Shield, title: 'Dealer-Only Access', description: 'Application-based onboarding keeps your pricing private and your network qualified.' },
  ];

  const trustBadges = [
    { label: 'UL Listed', sub: 'Safety certified' },
    { label: 'NSF Certified', sub: 'Sanitation grade' },
    { label: 'Miami-Dade Approved', sub: 'Hurricane rated' },
    { label: '50+ Years', sub: 'US manufacturing' },
  ];

  const steps = [
    {
      step: '01',
      title: 'Configure Your Unit',
      desc: 'Select cooler or freezer, set dimensions, pick doors, refrigeration and options — all with a live 3D preview.',
    },
    {
      step: '02',
      title: 'Review & Send',
      desc: 'See itemized pricing, apply your customer markup, and send your customer a branded PDF.',
    },
    {
      step: '03',
      title: 'Sign & Submit',
      desc: 'Customer signs in-portal. Convert to a Purchase Order and send straight to the factory.',
    },
  ];

  return (
    <>
      <Seo title="Amerikooler Quick Ship — Dealer Pricing & Quoting Portal" description="Configure, price, and quote Amerikooler walk-in coolers and freezers in minutes. Instant dealer pricing, freight, 3D previews, and PDF quotes." path="/" />
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35 }}
        className="sticky top-0 z-50 border-b border-border/60 bg-card/80 backdrop-blur-xl"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6">
          <img src={logo} alt="Amerikooler" width={150} height={40} decoding="async" className="h-10 w-auto" />
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/login')} className="rounded-xl">
              Sign In
            </Button>
            <Button onClick={() => navigate('/login?signup=true')} className="gap-2 rounded-xl">
              Get Started
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </motion.nav>

      {/* Hero — split layout: copy left, product mockup right */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[hsl(210,28%,10%)] via-[hsl(210,22%,14%)] to-[hsl(205,55%,22%)]" />
        <div className="absolute inset-0 opacity-[0.07]">
          <img src={heroImage} alt="" width={1600} height={867} loading="lazy" decoding="async" aria-hidden="true" className="h-full w-full object-cover" />
        </div>
        {/* Soft glow */}
        <div className="absolute -top-40 -right-32 h-[520px] w-[520px] rounded-full bg-primary/30 blur-[120px]" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-4 pt-16 pb-20 sm:px-6 sm:pt-20 sm:pb-28 lg:pt-24 lg:pb-32">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-10 lg:items-center">
            {/* Left — Copy */}
            <div className="max-w-xl">
              <motion.div
                variants={fadeUp} initial="hidden" animate="visible" custom={0}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur-sm mb-6"
              >
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                The Amerikooler dealer portal
              </motion.div>
              <motion.h1
                variants={fadeUp} initial="hidden" animate="visible" custom={1}
                className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-[3.5rem] lg:leading-[1.05] text-white"
              >
                Configure & Price{' '}
                <span className="text-primary">Walk-In Units</span>{' '}
                in Minutes
              </motion.h1>
              <motion.p
                variants={fadeUp} initial="hidden" animate="visible" custom={2}
                className="mt-6 max-w-lg text-lg text-white/70 leading-relaxed"
              >
                The fastest way for authorized dealers to configure, price, and quote pre-engineered walk-in coolers and freezers — with real-time 3D preview, in-portal signing, and instant PDFs.
              </motion.p>
              <motion.div
                variants={fadeUp} initial="hidden" animate="visible" custom={3}
                className="mt-8 flex flex-wrap gap-3"
              >
                <Button
                  size="lg"
                  onClick={() => navigate('/login?signup=true')}
                  className="gap-2 text-base px-7 rounded-xl h-12 shadow-lg shadow-primary/30"
                >
                  Apply for Dealer Access
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => navigate('/login')}
                  className="text-base px-7 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white rounded-xl h-12"
                >
                  Sign In
                </Button>
              </motion.div>
              <motion.div
                variants={fadeUp} initial="hidden" animate="visible" custom={4}
                className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60"
              >
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Authorized dealers only
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  Quick approval process
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  No setup fees
                </div>
              </motion.div>
            </div>

            {/* Right — Product Mockup (real UI cards in HTML/CSS) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="relative hidden lg:block"
              aria-hidden="true"
            >
              {/* Browser chrome card */}
              <div className="relative rounded-2xl bg-card shadow-2xl shadow-black/50 border border-white/10 overflow-hidden">
                {/* Title bar */}
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-border/40 bg-card">
                  <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                  <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                  <span className="h-3 w-3 rounded-full bg-[#28C840]" />
                  <span className="ml-3 text-[10px] text-muted-foreground font-mono">amerikoolerqs.com / new quote</span>
                </div>
                {/* Mock body */}
                <div className="p-5 space-y-4 bg-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Step 4 · Equipment</p>
                      <p className="text-base font-bold text-foreground mt-0.5">8' × 8' × 7'7" Cooler</p>
                    </div>
                    <span className="text-xs text-success bg-success/10 border border-success/20 px-2 py-0.5 rounded-md font-medium">Quick Ship</span>
                  </div>

                  {/* Real product render */}
                  <div className="relative h-52 rounded-xl bg-gradient-to-br from-[hsl(210,30%,97%)] via-[hsl(210,20%,94%)] to-[hsl(210,25%,91%)] dark:from-muted/30 dark:via-muted/15 dark:to-muted/30 border border-border/40 overflow-hidden">
                    <Suspense
                      fallback={
                        <div
                          className="absolute inset-0 flex items-center justify-center"
                          aria-label="Loading 3D viewer"
                          role="status"
                        >
                          <span className="h-10 w-10 rounded-full border-2 border-muted-foreground/20 border-t-primary animate-spin" />
                        </div>
                      }
                    >
                      <HeroProductViewer3D />
                    </Suspense>
                    <div className="absolute top-2 right-2 flex items-center gap-1 bg-background/80 backdrop-blur px-1.5 py-0.5 rounded shadow-sm pointer-events-none">
                      <span className="h-1.5 w-1.5 rounded-full bg-success animate-pulse" />
                      <span className="text-[9px] text-muted-foreground font-medium tracking-wide">LIVE 3D</span>
                    </div>
                    <div className="absolute bottom-1.5 left-2 text-[9px] text-muted-foreground/60 font-medium tracking-wide pointer-events-none select-none">
                      Drag to rotate
                    </div>
                  </div>

                  {/* Pricing breakdown */}
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between text-muted-foreground"><span>Box (panels)</span><span className="tabular-nums text-foreground">$8,420</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Refrigeration</span><span className="tabular-nums text-foreground">$3,950</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Options</span><span className="tabular-nums text-foreground">$680</span></div>
                    <div className="flex justify-between text-muted-foreground"><span>Freight (FL)</span><span className="tabular-nums text-foreground">$1,200</span></div>
                    <div className="flex justify-between pt-2 border-t border-border/50">
                      <span className="font-semibold text-foreground">Dealer Net</span>
                      <span className="tabular-nums font-bold text-primary text-base">$14,250</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-1" aria-hidden="true">
                    <div className="flex-1 h-9 rounded-lg bg-primary text-primary-foreground text-xs font-semibold flex items-center justify-center select-none pointer-events-none">Generate PDF</div>
                    <div className="h-9 px-3 rounded-lg border border-border text-xs font-medium text-foreground flex items-center justify-center select-none pointer-events-none">Email PDF</div>
                  </div>
                </div>
              </div>

              {/* Floating chips — positioned outside the card to avoid covering content */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9, duration: 0.5 }}
                className="absolute -left-8 -bottom-12 hidden xl:flex items-center gap-2 rounded-xl bg-card shadow-xl border border-border/60 px-3 py-2"
              >
                <div className="h-7 w-7 rounded-lg bg-success/10 flex items-center justify-center">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground leading-tight">Customer signed</p>
                  <p className="text-xs font-semibold text-foreground leading-tight">2 min ago</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.1, duration: 0.5 }}
                className="absolute -right-6 -top-12 hidden xl:flex items-center gap-2 rounded-xl bg-card shadow-xl border border-border/60 px-3 py-2"
              >
                <div className="h-7 w-7 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Zap className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground leading-tight">Quote ready</p>
                  <p className="text-xs font-semibold text-foreground leading-tight">in 3:42</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-b border-border/60 bg-card">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
          <p className="text-center text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-5">
            Trusted certifications & US manufacturing
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {trustBadges.map((b) => (
              <div
                key={b.label}
                className="flex items-center gap-3 rounded-xl border border-border/50 bg-background/50 px-4 py-3"
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/8 text-primary">
                  <Award className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground leading-tight">{b.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{b.sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} className="text-center mb-12">
          <h2 className="text-3xl font-bold text-foreground sm:text-4xl">Everything You Need to Quote Faster</h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
            A purpose-built portal for walk-in cooler and freezer dealers — not a generic CPQ.
          </p>
        </motion.div>
        <motion.div variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((f) => (
            <motion.div key={f.title} variants={fadeUp}>
              <div className="group ios-card hover:shadow-md transition-all p-6 h-full">
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/8 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  <f.icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-semibold text-foreground">{f.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* How it works — with mini visuals */}
      <section className="bg-card border-y border-border/60">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} className="text-center mb-14">
            <h2 className="text-3xl font-bold text-foreground sm:text-4xl">How It Works</h2>
            <p className="mt-4 text-lg text-muted-foreground">Three simple steps from spec to factory.</p>
          </motion.div>
          <motion.div
            variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
            className="grid gap-6 md:grid-cols-3"
          >
            {steps.map((s, idx) => (
              <motion.div
                key={s.step}
                variants={fadeUp}
                className="relative rounded-2xl border border-border/60 bg-background p-6 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold text-sm shadow-md">
                    {s.step}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
                </div>

                {/* Mini visual per step */}
                <div className="relative h-32 rounded-xl bg-gradient-to-br from-muted/50 to-muted/20 border border-border/40 mb-4 overflow-hidden">
                  {idx === 0 && (
                    <>
                      <img
                        src={walkinRender}
                        alt="Walk-in cooler product render"
                        width={128}
                        height={128}
                        className="absolute inset-0 w-full h-full object-contain p-1"
                        loading="lazy"
                        decoding="async"
                      />
                      <span className="absolute bottom-1.5 right-2 text-[8px] font-mono text-muted-foreground bg-background/70 backdrop-blur px-1.5 py-0.5 rounded">8' × 8' × 7'7"</span>
                    </>
                  )}
                  {idx === 1 && (
                    <div className="p-3 h-full flex flex-col justify-center text-[10px] space-y-1">
                      <div className="flex justify-between text-muted-foreground"><span>Box</span><span className="tabular-nums text-foreground">$8,420</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>Refrigeration</span><span className="tabular-nums text-foreground">$3,950</span></div>
                      <div className="flex justify-between text-muted-foreground"><span>Options + Freight</span><span className="tabular-nums text-foreground">$1,880</span></div>
                      <div className="flex justify-between pt-1 border-t border-border/50">
                        <span className="font-semibold text-foreground">Dealer Net</span>
                        <span className="tabular-nums font-bold text-primary">$14,250</span>
                      </div>
                      <div className="flex gap-1 pt-1">
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-medium">PDF</span>
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Email PDF</span>
                      </div>
                    </div>
                  )}
                  {idx === 2 && (
                    <div className="p-3 h-full flex flex-col justify-center gap-2">
                      <div className="rounded-md bg-background border border-border/60 px-2 py-1.5 flex items-center gap-1.5">
                        <FileSignature className="h-3 w-3 text-primary" />
                        <div className="flex-1 h-1.5 bg-primary/30 rounded-full">
                          <div className="h-full w-3/4 bg-primary rounded-full" />
                        </div>
                        <span className="text-[8px] font-medium text-success">Signed</span>
                      </div>
                      <div className="rounded-md bg-success/8 border border-success/30 px-2 py-1.5 flex items-center justify-between">
                        <span className="text-[10px] font-semibold text-foreground">PO-2026-0148</span>
                        <span className="text-[8px] px-1.5 py-0.5 rounded bg-success/15 text-success font-medium">Sent to Factory</span>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="relative rounded-3xl overflow-hidden p-8 sm:p-14 text-center shadow-xl"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[hsl(205,70%,18%)] via-[hsl(210,28%,14%)] to-[hsl(205,80%,28%)]" />
          <div className="absolute -top-20 -right-20 h-80 w-80 rounded-full bg-primary/40 blur-[100px]" aria-hidden="true" />
          <div className="absolute -bottom-20 -left-20 h-80 w-80 rounded-full bg-primary/20 blur-[100px]" aria-hidden="true" />
          <div className="relative">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Ready to Quote in Minutes, Not Days?</h2>
            <p className="mt-4 text-lg text-white/70 max-w-xl mx-auto">
              Join the Amerikooler dealer network and get instant access to configure, price, and close walk-in projects.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button size="lg" onClick={() => navigate('/login?signup=true')} className="gap-2 text-base px-8 rounded-xl h-12 shadow-lg shadow-primary/30">
                Apply for Dealer Access
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/login')}
                className="text-base px-8 bg-white/10 border-white/20 text-white hover:bg-white/20 hover:text-white rounded-xl h-12"
              >
                Sign In
              </Button>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-card">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
          <div className="grid gap-8 md:grid-cols-3">
            {/* Brand */}
            <div>
              <img src={logo} alt="Amerikooler" width={150} height={40} loading="lazy" decoding="async" className="h-10 w-auto mb-3" />
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                Pre-engineered walk-in coolers and freezers, manufactured in the USA for over 50 years.
              </p>
            </div>

            {/* Contact */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Contact</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2 text-muted-foreground">
                  <Phone className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <a href={`tel:${COMPANY_TRUST.phoneTel}`} className="hover:text-primary transition-colors">{COMPANY_TRUST.phone}</a>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <a href={`mailto:${COMPANY_TRUST.email}`} className="hover:text-primary transition-colors">{COMPANY_TRUST.email}</a>
                </li>
                <li className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                  <span>Manufactured in the USA</span>
                </li>
              </ul>
            </div>

            {/* Links */}
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">Resources</p>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="https://amerikooler.com" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary transition-colors">
                    Corporate site →
                  </a>
                </li>
                <li>
                  <button onClick={() => navigate('/login?signup=true')} className="text-muted-foreground hover:text-primary transition-colors">
                    Become a dealer
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/terms')} className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/privacy')} className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-border/50 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Amerikooler. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground">
              Quick Ship — Dealer Pricing Portal
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
